import { useCallback, useMemo, useState } from 'react';

import { FeatureGateEnvironment } from '@atlaskit/feature-gate-js-client';
import { FeatureGatesInitialization } from '@atlassian/feature-gates-react';
import Loader from 'components/common/loader/Loader';
import { captureException } from '@sentry/react';
import { STATSIG_STATE } from 'constants/index';
import useLoggedInUser from 'utils/useLoggedInUser';
// import { PageError } from 'components/errors/PageError';
// import { NotFound } from 'assets/images';

const statsigEnvironment = () => {
  switch (process.env.REACT_APP_ENVI) {
    case 'production':
      return FeatureGateEnvironment.Production;
    case 'staging':
      return FeatureGateEnvironment.Staging;
    default:
      return FeatureGateEnvironment.Development;
  }
};

type InitializerProps = {
  children: React.ReactNode;
};

// Function to get cookie value by name
const getCookieValue = (name: string): string | null => {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match && match[2] ? decodeURIComponent(match[2]) : null;
};

export const StatsigInitializer = (props: InitializerProps) => {
  const [statsigState, setStatsigState] = useState(STATSIG_STATE.LOADING);

  const user = useLoggedInUser();

  const identifiers = useMemo(() => {
    const anonymousId = getCookieValue('ajs_anonymous_id');
    return {
      atlassianAccountId: user?.accountId || '',
      analyticsAnonymousId: anonymousId || '',
    };
  }, [user]);

  const statsigRenderByState = useCallback(
    (currentStatsigState: string) => {
      switch (currentStatsigState) {
        case STATSIG_STATE.INITIALIZED:
          return props.children;
        case STATSIG_STATE.LOADING:
          return <Loader />;
        case STATSIG_STATE.ERROR:
        default:
          return <>{props.children}</>;
        // TODO: fix this by ensure experiment does not fail in anonymous state.
        // return (
        //   <PageError
        //     image={NotFound}
        //     heading="Unknown error while loading profile"
        //     description="There was some unknown error while loading the profile. Please try again later or reach out to support."
        //     link="/"
        //     linkText="Go home"
        //   />
        // );
      }
    },
    [props.children],
  );
  const onStatsigReady = useCallback((err: Error | undefined): void => {
    if (err) {
      setStatsigState(STATSIG_STATE.ERROR);
      captureException(err, {
        data: {
          message:
            'Statsig initialization failed, but FeatureGatesInitialization was loaded',
          environment: statsigEnvironment,
          identifiers,
        },
      });
    } else {
      setStatsigState(STATSIG_STATE.INITIALIZED);
    }
  }, []);

  // parcel throws error while trying to destructure (process.env || {})
  // https://github.com/parcel-bundler/parcel/issues/2984
  if (
    process.env.REACT_APP_STATSIG_DISABLED &&
    process.env.REACT_APP_ENVI === 'local'
  ) {
    return <>{props.children}</>;
  }

  return (
    <FeatureGatesInitialization
      options={{
        apiKey: process.env.REACT_APP_STATSIG_API_KEY || '',
        environment: statsigEnvironment(),
        targetApp: 'unified-portable-profile_web',
      }}
      identifiers={identifiers}
      onReady={onStatsigReady}
      initializingComponent={<Loader />}
    >
      {statsigRenderByState(statsigState)}
    </FeatureGatesInitialization>
  );
};
